import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { api, formatApiErrorDetail } from "@/lib/api";

const today = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

export const LienWaiverDialog = ({ open, onClose, p, onSaved }) => {
  const [form, setForm] = useState(() => ({
    kind: "full",
    name: p.customer_company || p.customer_name || "",
    address: p.job_address || "",
    total: String(p.accepted_total || p.quoted_total || ""),
    paid: String(p.paid_total || ""),
    date: today(),
    subs: [],
    save: true,
    email: false,
    email_to: "",
    signOnScreen: false,
    signature: "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    api.get(`/projects/${p.id}/lien-waiver/defaults`).then(({ data }) =>
      setForm((f) => ({ ...f, name: f.name || data.name, address: f.address || data.address, email_to: data.email || "" })),
    ).catch(() => {});
  }, [p.id]);

  const setSub = (i, k, v) => setForm((f) => ({ ...f, subs: f.subs.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));

  const generate = async () => {
    setBusy(true);
    try {
      const payload = {
        kind: form.kind,
        name: form.name,
        address: form.address,
        total: form.total === "" ? null : Number(form.total),
        paid: form.kind === "conditional" && form.paid !== "" ? Number(form.paid) : null,
        date: form.date,
        subs: form.subs,
        save: form.save,
        email_to: form.email ? form.email_to.trim() : "",
        signature_png: form.signOnScreen ? form.signature : "",
      };
      const { data: res } = await api.post(`/projects/${p.id}/lien-waiver`, payload);
      const bytes = Uint8Array.from(atob(res.pdf_b64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const bits = [res.saved && "saved to Drawings & Files", res.emailed_to && `emailed to ${res.emailed_to}`].filter(Boolean);
      toast.success(bits.length ? `Lien waiver ${bits.join(" and ")}` : "Lien waiver generated");
      if (res.saved) onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not build the lien waiver");
    }
    setBusy(false);
  };

  const field = (label, key, testid, extra = {}) => (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input data-testid={testid} value={form[key]} onChange={set(key)} className="h-10 border-zinc-800 bg-zinc-900" {...extra} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-zinc-800 bg-zinc-950 sm:max-w-lg" data-testid="lien-waiver-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText size={18} weight="bold" className="text-amber-300" /> Lien waiver — quick generator
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2" data-testid="lien-kind">
          {[
            ["full", "Full & final", "Unconditional — payment received in full"],
            ["conditional", "Conditional partial", "Covers Work paid to date; conditioned on the balance"],
          ].map(([v, l, s]) => (
            <button
              key={v}
              type="button"
              data-testid={`lien-kind-${v}`}
              onClick={() => set("kind")(v)}
              className={`rounded-md border px-3 py-2 text-left transition-colors ${
                form.kind === v ? "border-amber-500/60 bg-amber-500/10" : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
              }`}
            >
              <div className="text-sm font-semibold text-zinc-100">{l}</div>
              <div className="text-[11px] text-zinc-500">{s}</div>
            </button>
          ))}
        </div>
        {field("Customer or organization name", "name", "lien-name")}
        {field("Job address", "address", "lien-address")}
        <div className="grid grid-cols-2 gap-3">
          {field("Total contract price ($)", "total", "lien-total", { type: "number", step: "0.01", min: "0" })}
          {form.kind === "conditional"
            ? field("Payment received to date ($)", "paid", "lien-paid", { type: "number", step: "0.01", min: "0" })
            : field("Date", "date", "lien-date")}
        </div>
        {form.kind === "conditional" && <div className="grid grid-cols-2 gap-3">{field("Date", "date", "lien-date")}</div>}

        <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-900/60 p-3" data-testid="lien-subs">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Section B — subcontractors &amp; suppliers</Label>
            <button
              type="button"
              data-testid="lien-sub-add"
              onClick={() => setForm((f) => ({ ...f, subs: [...f.subs, { name: "", address: "" }] }))}
              disabled={form.subs.length >= 5}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-40"
            >
              <Plus size={12} weight="bold" /> Add
            </button>
          </div>
          {form.subs.length === 0 ? (
            <p className="text-xs text-zinc-600">None — leave empty to keep "None" circled on the form.</p>
          ) : (
            form.subs.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_1.3fr_auto] items-center gap-2">
                <Input data-testid={`lien-sub-name-${i}`} placeholder="Name" value={s.name} onChange={(e) => setSub(i, "name", e.target.value)} className="h-9 min-w-0 border-zinc-800 bg-zinc-900 text-sm" />
                <Input data-testid={`lien-sub-address-${i}`} placeholder="Address" value={s.address} onChange={(e) => setSub(i, "address", e.target.value)} className="h-9 min-w-0 border-zinc-800 bg-zinc-900 text-sm" />
                <button type="button" data-testid={`lien-sub-remove-${i}`} onClick={() => setForm((f) => ({ ...f, subs: f.subs.filter((_, j) => j !== i) }))} className="text-zinc-500 hover:text-red-400">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="space-y-2 rounded-md border border-zinc-800 p-3" data-testid="lien-sign-block">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-zinc-200">Sign on screen</div>
              <div className="text-[11px] text-zinc-500">Draw the manager's signature now instead of using the stored one</div>
            </div>
            <Switch data-testid="lien-sign-toggle" checked={form.signOnScreen} onCheckedChange={set("signOnScreen")} />
          </div>
          {form.signOnScreen && (
            <SignaturePad onChange={set("signature")} dataTestId="lien-signature-pad" clearTestId="lien-signature-clear" height={140} />
          )}
        </div>

        <div className="space-y-2 rounded-md border border-zinc-800 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-200">Save to Drawings &amp; Files</span>
            <Switch data-testid="lien-save-toggle" checked={form.save} onCheckedChange={set("save")} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-200">Email the waiver</span>
            <Switch data-testid="lien-email-toggle" checked={form.email} onCheckedChange={set("email")} />
          </div>
          {form.email && (
            <Input data-testid="lien-email-to" type="email" placeholder="customer@example.com" value={form.email_to} onChange={set("email_to")} className="h-9 border-zinc-800 bg-zinc-900 text-sm" />
          )}
        </div>

        <Button
          data-testid="lien-generate"
          onClick={generate}
          disabled={busy || !form.name.trim() || !form.address.trim() || (form.email && !form.email_to.trim()) || (form.signOnScreen && !form.signature)}
          className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          {busy ? "Building…" : "Generate PDF"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
